<script lang="ts" setup>
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import { AdvancedMarker } from "vue3-google-map";
import { computed, ref } from "vue";
import { TruckStatus } from "@/constants/truck";
import { DriverStatusOnHold, OwnerStatus } from "@/constants/user";
import type { TruckLocationSource } from "@/types/trucks";
import UserTruck from "@/components/user/UserTruck.vue";

const props = defineProps<{
  truck: TruckDto;
  locationSource: TruckLocationSource;
}>();

const isDetail = ref<boolean>(false);

const position = computed(() => {
  if (props.locationSource === "current" && props.truck.currentLocation?.coordinates) {
    return {
      lat: props.truck.currentLocation.coordinates.lat,
      lng: props.truck.currentLocation.coordinates.lon,
    };
  } else if (props.truck.availableLocation?.coordinates) {
    return {
      lat: props.truck.availableLocation.coordinates.lat,
      lng: props.truck.availableLocation.coordinates.lon,
    };
  }

  return null;
});

const zIndexMoreThanCluster = 9999999;

const markerOptions = computed(() => ({
  position: position.value ?? undefined,
  zIndex: isDetail.value ? zIndexMoreThanCluster : undefined,
}));

const statusClasses = computed(() => {
  const onHoldClass = "bg-red-lighten-5 text-red-lighten-1 border-red-lighten-1";
  const notAvailableClass = "bg-grey-lighten-4 text-grey-darken-1 border-grey-darken-1";
  const isOwnerOnHold = props.truck.owner.ownerStatus === OwnerStatus.OnHold;
  const isDriverOneOnHold =
    props.truck.driver?.driverStatus && DriverStatusOnHold.includes(props.truck.driver.driverStatus);

  if (props.truck?.archived) {
    return notAvailableClass;
  }

  if (isOwnerOnHold || isDriverOneOnHold) {
    return onHoldClass;
  }

  switch (props.truck.status) {
    case TruckStatus.Available:
      return "bg-green-lighten-5 text-green border-green";
    case TruckStatus.InRoute:
      return "bg-indigo-lighten-5 text-indigo border-indigo";
    case TruckStatus.WillBeAvailable:
      return "bg-amber-lighten-5 text-amber-darken-3 border-amber-darken-3";
    case TruckStatus.NotAvailable:
      return notAvailableClass;
    case TruckStatus.OnHold:
      return onHoldClass;
    default:
      return notAvailableClass;
  }
});
</script>

<template>
  <advanced-marker v-if="position" :options="markerOptions">
    <template #content>
      <div :class="statusClasses" class="truck-marker" @click.stop="isDetail = !isDetail">
        <v-icon>mdi-truck</v-icon>
        <span class="font-weight-500">{{ truck.number }}</span>

        <v-icon v-if="truck.exclusive" color="amber" size="14">mdi-star</v-icon>

        <transition name="truck-popup">
          <div v-if="isDetail" class="truck-marker__popup" @click.stop>
            <user-truck :truck="truck" :truck-number-size="20" hide-icon />

            <v-icon class="truck-marker__close" color="grey-darken-2" size="24" @click.stop="isDetail = false">
              mdi-close
            </v-icon>
          </div>
        </transition>
      </div>
    </template>
  </advanced-marker>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.truck-marker {
  position: relative;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-width: 2px;
  cursor: pointer;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 20%;
    transform: translateX(-10%);
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-top: 9px solid;
    border-top-color: inherit;
  }

  &__popup {
    position: absolute;
    bottom: calc(100% + 14px);
    left: 50%;
    transform: translateX(-50%);
    padding: 20px 36px 20px 20px;
    background-color: $white;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    white-space: nowrap;
  }

  &__close {
    position: absolute;
    top: 8px;
    right: 8px;
    cursor: pointer;
  }
}

.truck-popup-enter-active,
.truck-popup-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.truck-popup-enter-from,
.truck-popup-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(6px) scale(0.96);
}

.truck-popup-enter-to,
.truck-popup-leave-from {
  opacity: 1;
  transform: translateX(-50%) translateY(0) scale(1);
}
</style>
